import { intelDealLevers, type DealLeversResponse } from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { AboutButton } from '../../ui/AboutThis';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { A, CAP, CARD, onSet, SectionRule, type TabProps } from './common';
import { K, M, MINUS, PPD, type Deal } from './model';
import { ModeBar } from './simulate/ModeBar';
import { BuildLabel } from '../../ui/buildLabels';

const RISKC = ['#1F6B52', '#5B6B7C', '#B3261E'];
const SOCC = [0.7, 0.75, 0.8, 0.85, 0.9];
const TILE_FIG = 'font-size:22px;font-weight:700;letter-spacing:-.02em;';
const TILE_FIG19 = 'font-size:19px;font-weight:700;letter-spacing:-.02em;';
/** A percentage the V4 way: the design's minus sign, and no "-0%". */
const pc = (x: number, d?: number) => {
  const t = (x * 100).toFixed(d === undefined ? 0 : d);
  return (/^-0(\.0+)?$/.test(t) ? t.slice(1) : t.replace('-', MINUS)) + '%';
};
/** A signed $M ("$0" when it rounds to nothing). */
const sgnM = (x: number) => (Math.abs(x) < 0.0005 ? '$0' : (x > 0 ? '+' : MINUS) + M(Math.abs(x)));

/**
 * Simulate > Levers (V4 aiIsScen, T16074; aiScenVals L31296): live what-if on the facility on
 * the right. Returns use the deal card's one method (f.ret: the Buy-both base case, exit
 * EBITDA compounding on the payer-blended curve, exit at the scenario multiple). A building
 * carried over from the map shows what is assumed about it. MODELED throughout.
 */
export function ScenarioTab(p: TabProps) {
  const { f, st, set } = p;
  return (
    <>
      <ModeBar p={p} tab="scenario" />
      <div style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
        <div>
          {/* V9: the explainer opens on the "i" (design h17497). */}
          <div style={sx('font-size:21px;font-weight:700;letter-spacing:-.018em;')}>
            Levers
            <AboutButton open={!!st.v9i?.levers} onToggle={() => set({ v9i: { ...st.v9i, levers: !st.v9i?.levers } })} gap={8} />
          </div>
          {st.v9i?.levers && <div style={sx('font-size:16px;color:#54606C;')}>Live what-if - drag levers, see value &amp; returns recompute.</div>}
        </div>
        {f ? <Scenario p={p} f={f} /> : <EmptyState title="Nothing to model yet" body="Track a facility from the catalog and model it here." />}
      </div>
    </>
  );
}

function Scenario({ p, f }: { p: TabProps; f: Deal }) {
  const { st, set, client } = p;
  const fromMap = f.status === 'Map';
  const SMULT = [-1, -0.5, 0, 0.5, 1].map((d) => Math.round((f.mult + d) * 10) / 10);
  const occ = st.scOcc ?? f.occ;
  const mix = st.scMix ?? f.priv;
  const marg = st.scMargin ?? f.margin;
  const mult = st.scMult ?? f.mult;
  const ppdOf = (m: number) => f.mcare * PPD.medicare + (1 - f.mcare - m) * PPD.medicaid + m * PPD.private;
  const ebOf = (o: number, m: number, g: number) => ((f.beds * o * 365 * ppdOf(m)) / 1e6) * g;
  const eb2 = ebOf(occ, mix, marg);
  const ev2 = eb2 * mult;
  const created = ev2 - f.entry;
  const { moic: moic2, irr: irr2 } = f.ret(eb2, mult);
  const b1 = ebOf(occ, f.priv, f.margin) * f.mult;
  const b2 = ebOf(occ, mix, f.margin) * f.mult;
  const b3 = ebOf(occ, mix, marg) * f.mult;
  const bridge: [string, number, boolean][] = [
    ['Entry EV', f.entry, false],
    ['Occupancy', b1 - f.entry, true],
    ['Payer mix', b2 - b1, true],
    ['Margin / ops', b3 - b2, true],
    ['Multiple expansion', ev2 - b3, true],
  ];

  const appetite = st.scAppetite || 'Moderate';
  const maxRisk = appetite === 'Conservative' ? 0 : appetite === 'Moderate' ? 1 : 2;
  const picked = st.scLevers || { 0: true, 1: true, 2: true, 3: true };
  const levers = useQuery<DealLeversResponse>(
    (signal) => intelDealLevers(client, { query: { appetite: appetite.toLowerCase() }, body: { facility: { name: f.name, state: f.stt, beds: f.beds, type: f.type } }, signal }),
    [client, f.id, appetite],
  );
  const LIB = levers.data?.levers ?? [];
  let upEV = 0;
  let upBps = 0;
  let nActive = 0;
  LIB.forEach((l, i) => {
    if (picked[i] && l.riskRank <= maxRisk) {
      upEV += l.evUpliftPct * f.entry;
      upBps += l.irrBps;
      nActive += 1;
    }
  });

  const caseOf = (dOcc: number, dMarg: number, dMult: number) => {
    const o = Math.max(0.55, Math.min(0.98, occ + dOcc));
    const g = Math.max(0.03, marg + dMarg);
    const m = Math.max(1.5, mult + dMult);
    const e = ebOf(o, mix, g);
    const ev = e * m;
    const rr = f.ret(e, m);
    return { e, ev, mo: rr.moic, irr: rr.irr, cr: ev - f.entry };
  };
  const cases = [
    { label: 'Bear', color: '#B3261E', c: caseOf(-0.06, -0.019, -0.49) },
    { label: 'Base', color: '#54606C', c: caseOf(0, 0, 0) },
    { label: 'Bull', color: '#1F6B52', c: caseOf(0.04, 0.019, 0.49) },
  ];
  const resetAll = { scOcc: undefined, scMix: undefined, scMargin: undefined, scMult: undefined };
  const srcLabel = levers.data ? (levers.data.source === 'live' ? 'LIVE · ' + levers.data.citations.length + ' cited sources' : 'MODELED · standard lever library · cited sources once research is connected') : '';

  return (
    <>
      {fromMap && (
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:4px;font-size:16px;color:#3C454F;line-height:1.45;padding:7px 0;border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);')}>
          <BuildLabel><span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#3C454F;background:rgba(34,39,46,.08);border-radius:4px;padding:2px 6px;')}>MODELED EST.</span></BuildLabel>
          <span>{'From the Relationship Map. Beds, occupancy and payer mix are its map facts. Assumed until its cost report loads: EBITDA at 10% of its payer revenue, a ' + f.mult.toFixed(1) + 'x entry multiple, staffing and quality. Values here use the multiple; Deal Desk shows the DCF, so the two differ.'}</span>
        </div>
      )}

      <SectionRule label="The deal" />
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:11px;')}>
        <Fig label="Pro-forma EBITDA" val={M(eb2)} valStyle={TILE_FIG19} note={'as-is ' + M(f.ebitda)} />
        <Fig label="Enterprise value" val={M(ev2)} valStyle={TILE_FIG19} note={'as-is ' + M(f.entry)} />
        <Fig
          label="Value created"
          val={sgnM(created)}
          valStyle={TILE_FIG + 'color:' + (created > 0.005 ? '#1F6B52' : created < -0.005 ? '#B3261E' : '#22272E') + ';'}
          note={pc(created / f.entry, 0) + ' vs entry'}
        />
        <Fig
          label="Return"
          val={pc(irr2) + ' IRR'}
          valStyle={TILE_FIG + 'color:' + (irr2 >= 0.2 ? '#1F6B52' : irr2 >= 0.12 ? '#5B6B7C' : irr2 < -0.005 ? '#B3261E' : '#22272E') + ';'}
          note={'MOIC ' + moic2.toFixed(1) + 'x · 5-yr'}
        />
      </div>

      <SectionRule label="Model" />
      <div style={sx('display:grid;grid-template-columns:minmax(0,1.35fr) minmax(0,1fr);gap:13px;')}>
        <div style={sx(CARD)}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
            <span style={sx(CAP)}>Assumptions</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>drag levers to model</span>
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:13px;')}>
            <Lever label="Occupancy" lbl={pc(occ)} asIs={'as-is ' + pc(f.occ)} min={0.6} max={0.98} step={0.01} value={occ} onChange={(v) => set({ scOcc: v })} />
            <Lever label="Private-pay mix" lbl={pc(mix)} asIs={'as-is ' + pc(f.priv)} min={0.1} max={0.6} step={0.01} value={mix} onChange={(v) => set({ scMix: v })} />
            <Lever label="EBITDA margin" lbl={pc(marg)} asIs={'as-is ' + pc(f.margin)} min={0.05} max={0.22} step={0.005} value={marg} onChange={(v) => set({ scMargin: v })} />
            <Lever label="Exit multiple" lbl={mult.toFixed(1) + 'x'} asIs={'entry ' + f.mult.toFixed(1) + 'x'} min={3} max={9} step={0.1} value={mult} onChange={(v) => set({ scMult: v })} />
          </div>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:15px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
            <span style={sx('font-size:16px;')}>Revenue / bed &middot; yr</span>
            <span style={sx('font-size:14px;color:#54606C;')}>{'blended $' + ppdOf(mix).toFixed(0) + '/day'}</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#1F6B52;')}>{K(f.beds ? ((eb2 / marg) * 1e6) / f.beds : 0)}</span>
          </div>
          <div style={sx('text-align:right;margin-top:5px;')}>
            <A onClick={onSet(set, resetAll)} style="font-size:14px;color:#2F5C97;text-decoration:none;">
              Reset to as-is
            </A>
          </div>
        </div>

        <div style={sx(CARD)}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
            <span style={sx(CAP)}>Value-creation bridge</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>EV walk from entry</span>
          </div>
          {bridge.map(([label, v, delta]) => (
            <div key={label} style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
              <span style={sx('color:#3C454F;')}>{label}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;color:' + (!delta ? '#22272E' : v > 0.005 ? '#1F6B52' : v < -0.005 ? '#B3261E' : '#54606C') + ';')}>
                {delta ? sgnM(v) : M(v)}
              </span>
            </div>
          ))}
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:12px;')}>
            <span style={sx('font-size:16px;font-weight:700;')}>Pro-forma EV</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:19px;font-weight:700;')}>{M(ev2)}</span>
          </div>
        </div>
      </div>

      <SectionRule label="Value stack" />
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-bottom:12px;')}>
          <span style={sx(CAP)}>Deal levers</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>legal value stack &middot; gated by risk appetite</span>
        </div>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-bottom:6px;')}>
          {['Conservative', 'Moderate', 'Aggressive'].map((a) => (
            <A
              key={a}
              onClick={onSet(set, { scAppetite: a })}
              style={
                'display:inline-flex;align-items:center;height:28px;padding:0 14px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
                (appetite === a ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);')
              }
            >
              {a}
            </A>
          ))}
          <span style={sx('flex:1;')} />
          {srcLabel && <span style={sx('font-size:14px;font-weight:600;letter-spacing:.04em;color:#54606C;background:rgba(34,39,46,.07);border-radius:4px;padding:3px 7px;min-width:0;max-width:100%;overflow-wrap:break-word;')}>{srcLabel}</span>}
        </div>
        {levers.data === null && levers.loading && <Skeleton rows={6} label="Loading the lever library" />}
        {levers.data === null && levers.error && <ErrorState error={levers.error} onRetry={levers.reload} />}
        {LIB.map((l, i) => {
          const locked = l.riskRank > maxRisk;
          const on = !!picked[i] && !locked;
          return (
            <A
              key={l.id}
              hover="intel-h-lever"
              label={l.name}
              onClick={locked ? undefined : onSet(set, { scLevers: { ...picked, [i]: !picked[i] } })}
              style={'display:flex;align-items:flex-start;gap:10px;padding:8px 0;text-decoration:none;color:#22272E;' + (locked ? 'opacity:.42;' : '')}
            >
              <span
                role="checkbox"
                aria-checked={on}
                style={sx(
                  'display:inline-flex;align-items:center;justify-content:center;width:15px;height:22px;flex:0 0 15px;border-radius:4px;font-size:14px;color:#fff;text-decoration:none;white-space:nowrap;' +
                    (locked ? 'background:rgba(34,39,46,.09);color:#54606C;border:1px solid rgba(34,39,46,.16);cursor:default;' : on ? 'background:#2F5C97;' : 'background:#FBF8F1;border:1px solid rgba(34,39,46,.28);'),
                )}
              >
                {on ? '✓' : locked ? '·' : ''}
              </span>
              <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:16px;font-weight:600;')}>{l.name}</span>
                  <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:600;color:#54606C;background:rgba(34,39,46,.07);white-space:nowrap;')}>{l.category}</span>
                  <span style={sx('display:inline-block;width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + RISKC[l.riskRank] + ';')} />
                </span>
                <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>{l.note}</span>
              </span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#1F6B52;')}>{'+' + M(l.evUpliftPct * f.entry)}</span>
            </A>
          );
        })}
        <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:11px;padding-top:11px;border-top:1px solid rgba(34,39,46,.1);')}>
          <span style={sx('font-size:16px;font-weight:600;')}>{nActive + ' lever' + (nActive === 1 ? '' : 's') + ' active'}</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#1F6B52;')}>{'+' + M(upEV) + ' EV · +' + upBps + ' bps IRR'}</span>
        </div>
        <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:4px;')}>
          <span style={sx('font-size:16px;')}>Levered enterprise value</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:19px;font-weight:700;')}>{M(f.entry + upEV)}</span>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-top:7px;')}>Structures are modeled, not advice - verify with counsel before pricing them into a bid.</div>
      </div>

      <SectionRule label="Outcomes" />
      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
          <span style={sx(CAP)}>Cases</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>bear &middot; base &middot; bull</span>
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:18px;')}>
          {cases.map(({ label, color, c }) => (
            <div key={label}>
              <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-bottom:9px;')}>
                <span style={sx('display:inline-block;width:7px;height:7px;border-radius:50%;background:' + color + ';')} />
                <span style={sx('font-size:16px;font-weight:700;')}>{label}</span>
              </div>
              {(
                [
                  ['IRR', pc(c.irr), c.irr >= 0.15 ? '#1F6B52' : c.irr >= 0.05 ? '#54606C' : '#B3261E'],
                  ['MOIC', c.mo.toFixed(1) + 'x', '#22272E'],
                  ['Enterprise value', M(c.ev), '#22272E'],
                  ['Value created', sgnM(c.cr), c.cr >= 0 ? '#1F6B52' : '#B3261E'],
                  ['Pro-forma EBITDA', M(c.e), '#22272E'],
                ] as [string, string, string][]
              ).map(([k, v, col]) => (
                <div key={k} style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;font-size:14px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                  <span style={sx('color:#54606C;')}>{k}</span>
                  <span style={sx('flex:1;')} />
                  <span style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:' + col + ';')}>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:13px;')}>
          <span style={sx(CAP)}>Sensitivity &middot; IRR</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>occupancy (rows) &times; exit multiple (cols)</span>
        </div>
        <div style={sx('display:grid;grid-template-columns:64px repeat(5,minmax(0,1fr));gap:6px;align-items:center;')}>
          <span style={sx('font-size:14px;color:#54606C;font-weight:600;')}>OCC \ x</span>
          {SMULT.map((m) => (
            <span key={m} style={sx('text-align:center;font-size:14px;color:#54606C;font-weight:600;')}>
              {m.toFixed(1) + 'x'}
            </span>
          ))}
        </div>
        {SOCC.map((o) => (
          <div key={o} style={sx('display:grid;grid-template-columns:64px repeat(5,minmax(0,1fr));gap:6px;align-items:center;margin-top:6px;')}>
            <span style={sx('font-size:14px;color:#54606C;font-weight:600;')}>{pc(o)}</span>
            {SMULT.map((m) => {
              const irr = f.ret(ebOf(o, mix, marg), m).irr;
              const bg = irr >= 0.25 ? 'rgba(47,138,111,.16)' : irr >= 0.15 ? 'rgba(79,138,214,.15)' : irr >= 0.08 ? 'rgba(91,107,124,.14)' : 'rgba(179,38,30,.13)';
              const col = irr >= 0.25 ? '#1F6B52' : irr >= 0.15 ? '#2F5C97' : irr >= 0.08 ? '#5B6B7C' : '#B3261E';
              return (
                <span key={m} style={sx('box-sizing:border-box;padding:9px 0;border-radius:7px;text-align:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;background:' + bg + ';color:' + col + ';')}>
                  {pc(irr)}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

function Fig({ label, val, valStyle, note }: { label: string; val: string; valStyle: string; note: string }) {
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
      <div style={sx(CAP + 'margin-bottom:5px;')}>{label}</div>
      <div style={sx(valStyle)}>{val}</div>
      <div style={sx('font-size:14px;color:#54606C;')}>{note}</div>
    </div>
  );
}

function Lever({ label, lbl, asIs, min, max, step, value, onChange }: { label: string; lbl: string; asIs: string; min: number; max: number; step: number; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('font-size:16px;')}>{label}</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#2F5C97;')}>{lbl}</span>
        <span style={sx('font-size:14px;color:#54606C;')}>{asIs}</span>
      </div>
      <input type="range" aria-label={label} min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} style={sx('margin:0;width:100%;accent-color:#2F5C97;')} />
    </div>
  );
}
